import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useState } from 'react';
import { useDataProvider } from 'react-admin';

import {
    fromDatetimeLocalValue,
    toDatetimeLocalValue,
} from '@/modules/leads/LeadNextStepsEditor';
import type { CrmDataProvider } from '@/providers/types';
import { parseBackendDate } from '@/utils/date_utils';
import { AppNotification } from './notificationApi';

function defaultRemindAt(): string {
    return toDatetimeLocalValue(
        new Date(Date.now() + 60 * 60 * 1000).toISOString()
    );
}

const LeadReminderActions = ({
    item,
    token,
    onResolved,
    onDialogOpenChange,
}: {
    item: AppNotification;
    token: string | null;
    onResolved: (id: number) => void;
    onDialogOpenChange?: (open: boolean) => void;
}) => {
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [busy, setBusy] = useState(false);
    const [snoozeOpen, setSnoozeOpen] = useState(false);
    const [remindAt, setRemindAt] = useState(defaultRemindAt);
    const [error, setError] = useState('');

    const stop = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    };

    const handleDone = async (event: React.MouseEvent) => {
        stop(event);
        if (!token || busy) return;
        setBusy(true);
        try {
            await dataProvider.doneNotification(item.id);
            onResolved(item.id);
        } catch {
            setBusy(false);
        }
    };

    const openSnooze = (event: React.MouseEvent) => {
        stop(event);
        setError('');
        setRemindAt(defaultRemindAt());
        onDialogOpenChange?.(true);
        setSnoozeOpen(true);
    };

    const closeSnooze = () => {
        if (busy) return;
        setSnoozeOpen(false);
        onDialogOpenChange?.(false);
        setError('');
    };

    const confirmSnooze = async () => {
        if (!token || busy) return;
        const local = fromDatetimeLocalValue(remindAt);
        if (!local) {
            setError('Pick a date and time');
            return;
        }
        const parsed = parseBackendDate(local);
        if (Number.isNaN(parsed.getTime())) {
            setError('Pick a date and time');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await dataProvider.snoozeNotification(item.id, parsed.toISOString());
            setSnoozeOpen(false);
            onDialogOpenChange?.(false);
            onResolved(item.id);
        } catch {
            setError('Could not snooze — try a future time');
            setBusy(false);
        }
    };

    return (
        <Stack
            direction="row"
            spacing={0.5}
            onClick={stop}
            onMouseDown={stop}
        >
            <Button
                size="small"
                onClick={handleDone}
                disabled={busy || !token}
                sx={{ minWidth: 0, px: 1 }}
            >
                Done
            </Button>
            <Button
                size="small"
                onClick={openSnooze}
                disabled={busy || !token}
                sx={{ minWidth: 0, px: 1 }}
            >
                Snooze
            </Button>
            <Dialog
                open={snoozeOpen}
                onClose={closeSnooze}
                onClick={stop}
                sx={{ zIndex: 1400 }}
            >
                <DialogTitle>Snooze reminder</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Remind at"
                        type="datetime-local"
                        value={remindAt}
                        onChange={e => setRemindAt(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSnooze} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={confirmSnooze} disabled={busy}>
                        Snooze
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default LeadReminderActions;
